/**
 * RoleGuard Component - Role-based access control for routes
 * Restricts access based on user role
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getUserRole } from '../../services/roleService';
import { UserRole } from '../../types/roles';
import './RoleGuard.css';
import { createComponentLogger } from '../../utils/logger';

const logger = createComponentLogger('RoleGuard');

// #48 fix (2026-05-17): module-level in-memory cache so RoleGuard doesn't
// re-fetch the role on every route mount. The cache is keyed by uid which
// means a sign-out + new sign-in for a different user WILL invalidate the
// previous entry (different uid → different key). This is the encyclopedia's
// "user-ID in cache key" finding from #48 search applied here directly.
// Long-term: switch to TanStack Query with `queryKey: ['userRole', uid]`,
// staleTime configurable, automatic invalidation on auth state change.
const ROLE_CACHE = new Map<string, { role: UserRole; cachedAt: number }>();
const ROLE_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const getCachedRole = (uid: string): UserRole | null => {
  const entry = ROLE_CACHE.get(uid);
  if (!entry) return null;
  if (Date.now() - entry.cachedAt > ROLE_CACHE_TTL_MS) {
    ROLE_CACHE.delete(uid);
    return null;
  }
  return entry.role;
};

/** Externally callable: invalidate one user's cached role after a role change. */
export const invalidateRoleCache = (uid?: string) => {
  if (uid) ROLE_CACHE.delete(uid);
  else ROLE_CACHE.clear();
};

interface RoleGuardProps {
  children: React.ReactNode;
  allowedRoles: UserRole[];
  fallbackPath?: string;
}

/**
 * RoleGuard Component
 *
 * Protects routes by checking if the user has one of the allowed roles.
 * If the user's role is not in the allowedRoles array, they are redirected.
 *
 * @example
 * <RoleGuard allowedRoles={['admin', 'district_admin']}>
 *   <AdminDashboard />
 * </RoleGuard>
 */
export const RoleGuard: React.FC<RoleGuardProps> = ({
  children,
  allowedRoles,
  fallbackPath = '/dashboard'
}) => {
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [roleLoading, setRoleLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);

  // S150: every call site passes an inline array literal
  // (`allowedRoles={['teacher', 'admin']}`, twelve of them in App.tsx), so the
  // prop is a new identity on every render. Keying the effect off the array
  // itself re-ran it on every render; keying it off the CONTENTS runs it only
  // when the answer could actually change.
  const allowedKey = allowedRoles.join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allowed = useMemo(() => allowedRoles, [allowedKey]);
  const uid = currentUser?.uid ?? null;

  useEffect(() => {
    // S150: this effect's three pieces of state answer one question — "may THIS
    // user reach a route restricted to THESE roles". When the user or the role
    // set changes, the previous answer is not a stale approximation of the new
    // one, it is the answer to a different question. Holding it rendered the
    // protected children to whoever signed in next, for as long as their
    // `users/{uid}` read took. On a shared classroom Chromebook that is a
    // student watching the teacher's page load. Reset first, decide second.
    setUserRole(null);
    setAccessDenied(false);
    setRoleLoading(true);

    let cancelled = false;

    const checkRole = async () => {
      try {
        // Wait for auth to load
        if (authLoading) {
          return;
        }

        // If no user, redirect to login/dashboard
        if (!uid) {
          logger.debug('No current user, redirecting to fallback path');
          navigate(fallbackPath);
          return;
        }

        // Fetch user role (cached for ROLE_CACHE_TTL_MS, keyed by uid)
        const cached = getCachedRole(uid);
        const role = cached ?? (await getUserRole(uid));
        if (!cached) {
          ROLE_CACHE.set(uid, { role, cachedAt: Date.now() });
        }

        // S150: the user may have changed while that read was in flight. A role
        // resolved for somebody who is no longer signed in must not open a gate.
        if (cancelled) return;

        setUserRole(role);

        // Check if role is allowed
        if (!allowed.includes(role)) {
          logger.warn(`User role '${role}' not in allowed roles: ${allowed.join(', ')}`);
          setAccessDenied(true);
          navigate(fallbackPath);
        }

        setRoleLoading(false);
      } catch (error) {
        if (cancelled) return;
        logger.error('Error checking user role:', error);
        setAccessDenied(true);
        navigate(fallbackPath);
        setRoleLoading(false);
      }
    };

    checkRole();

    return () => {
      cancelled = true;
    };
  }, [uid, authLoading, allowed, navigate, fallbackPath]);

  // Show loading state
  if (authLoading || roleLoading) {
    return (
      <div className="role-guard-loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Show access denied state
  if (accessDenied) {
    return (
      <div className="role-guard-denied">
        <div className="error-container">
          <h2>Access Denied</h2>
          <p>You do not have permission to access this page.</p>
          <button onClick={() => navigate(fallbackPath)}>Go to Dashboard</button>
        </div>
      </div>
    );
  }

  // Render children if user has allowed role
  if (userRole && allowed.includes(userRole)) {
    return <>{children}</>;
  }

  // Fallback to loading
  return (
    <div className="role-guard-loading">
      <div className="spinner"></div>
      <p>Verifying access...</p>
    </div>
  );
};

// #47 fix (2026-05-17): styles moved to RoleGuard.css (sibling file) and
// imported via a normal `import './RoleGuard.css'` so they're processed by
// the build (minified, cached, CSP-friendly) and the gradient colors use
// CSS variables that match the rest of the app instead of the standalone
// purple-blue scheme. Old runtime `document.createElement('style')` inject
// was duplicated each time the module loaded, ran on first import even if
// the component never rendered, and bypassed any CSP `style-src` policy.
